import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../api';
import BottomNav from '../components/BottomNav';
import Toast, { useToast } from '../components/Toast';

export default function CartPage() {
  const { storeId, tableId } = useParams();
  const { items, removeItem, updateQuantity, getTotal, clearCart, sessionId, setSession } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [removingIdx, setRemovingIdx] = useState(null);
  const [poppingIdx, setPoppingIdx] = useState(null);
  const [priceKey, setPriceKey] = useState(0);

  const { toast, showToast } = useToast();

  const [couponCode, setCouponCode] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState('');

  const total = getTotal();
  const finalTotal = Math.max(0, total - couponDiscount);

  const handleAnimatedRemove = useCallback((idx) => {
    setRemovingIdx(idx);
    setTimeout(() => {
      removeItem(idx);
      setRemovingIdx(null);
      setPriceKey(k => k + 1);
    }, 250);
  }, [removeItem]);

  const handleQtyChange = useCallback((idx, newQty) => {
    if (newQty < 1) {
      handleAnimatedRemove(idx);
    } else {
      updateQuantity(idx, newQty);
      setPoppingIdx(idx);
      setPriceKey(k => k + 1);
      setTimeout(() => setPoppingIdx(null), 200);
    }
  }, [updateQuantity, handleAnimatedRemove]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponApplying(true);
    setCouponError('');
    try {
      const result = await api.post('/coupons/validate', {
        code: couponCode.trim(),
        store_id: storeId,
        order_amount: total,
      });
      const data = result?.data || result;
      const discount = data?.discount_amount || data?.discount || 0;
      if (discount > 0) {
        setCouponDiscount(discount);
        setCouponInfo(data);
        showToast(`쿠폰이 적용되었습니다 (-${discount.toLocaleString()}원)`, 'success');
      } else {
        setCouponError('적용할 수 없는 쿠폰입니다');
      }
    } catch (err) {
      setCouponError(err?.message || '쿠폰 적용에 실패했습니다');
      setCouponDiscount(0);
      setCouponInfo(null);
    } finally {
      setCouponApplying(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponDiscount(0);
    setCouponInfo(null);
    setCouponError('');
  };

  const handleSubmitOrder = async () => {
    if (items.length === 0) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        tableId,
        storeId,
        sessionId,
        items: items.map((it) => ({
          menu_id: it.id || it.menuId,
          menu_name: it.name,
          unit_price: it.price,
          quantity: it.quantity,
          options: Array.isArray(it.options) ? it.options : [],
        })),
        totalAmount: finalTotal,
      };

      if (couponInfo) {
        payload.coupon_code = couponCode.trim();
        payload.coupon_discount = couponDiscount;
      }

      const result = await api.post('/orders', payload);
      const newSessionId = result?.session_id || result?.sessionId;

      const targetSession = newSessionId || sessionId;
      if (newSessionId && newSessionId !== sessionId) {
        setSession(newSessionId);
        localStorage.setItem('sessionId', newSessionId);
      }

      clearCart();
      if (targetSession) {
        navigate(`/orders/${targetSession}`);
      }
    } catch (err) {
      setError(err.message || '주문에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="page">
        <header className="page-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="page-header-title">장바구니</span>
          <div style={{ width: 36 }} />
        </header>
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
          </div>
          <div className="empty-state-title">장바구니가 비어있어요</div>
          <div className="empty-state-text">메뉴를 선택해 장바구니에 담아보세요</div>
          <button
            className="btn btn-primary btn-lg"
            style={{ marginTop: 20 }}
            onClick={() => navigate(`/table/${storeId}/${tableId}`)}
          >
            메뉴 보러 가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="page" style={{ paddingBottom: 'calc(60px + var(--safe-bottom))' }}>
      <header className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="page-header-title">장바구니</span>
        <button
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-tertiary)',
            fontSize: 13,
            fontFamily: 'var(--font)',
            fontWeight: 500,
            padding: '4px 8px',
          }}
          onClick={clearCart}
        >
          전체 삭제
        </button>
      </header>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Cart items */}
        <div style={{ padding: '12px 20px 0' }}>
          {items.map((item, idx) => (
            <div
              key={idx}
              className={`animate-fade-in-up ${removingIdx === idx ? 'cart-item-removing' : ''}`}
              style={{
                padding: '16px 0',
                borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
                animationDelay: `${idx * 40}ms`,
              }}
            >
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    item.emoji ? item.emoji : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-quaternary)" strokeWidth="1.5">
                        <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                      </svg>
                    )
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2, color: 'var(--text)' }}>{item.name}</div>
                  {item.options && item.options.length > 0 && (
                    <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>
                      {item.options.map((opt) => opt.name).join(' · ')}
                    </div>
                  )}
                  <div style={{ fontSize: 14, color: 'var(--text-tertiary)', marginBottom: 10 }}>
                    {item.price.toLocaleString()}원
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="qty-stepper">
                      <button
                        className="qty-btn"
                        onClick={() => handleQtyChange(idx, item.quantity - 1)}
                      >
                        -
                      </button>
                      <span className={`qty-value ${poppingIdx === idx ? 'qty-pop' : ''}`}>{item.quantity}</span>
                      <button className="qty-btn" onClick={() => handleQtyChange(idx, item.quantity + 1)}>
                        +
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span key={`price-${idx}-${item.quantity}`} className="price-animated" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                        {(item.subtotal || 0).toLocaleString()}원
                      </span>
                      <button
                        onClick={() => handleAnimatedRemove(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-quaternary)',
                          padding: 4,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Section divider */}
        <div style={{ height: 8, background: 'var(--border)', margin: '8px 0' }} />

        {/* Coupon section */}
        <div style={{ padding: '16px 20px' }}>
          <div className="section-title">쿠폰 적용</div>
          {couponInfo ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--success)' }}>
                  {couponInfo.coupon_name || couponCode} 적용됨
                </div>
                <div style={{ fontSize: 13, color: 'var(--success)', marginTop: 2 }}>
                  -{couponDiscount.toLocaleString()}원 할인
                </div>
              </div>
              <button
                onClick={handleRemoveCoupon}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-quaternary)',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                    setCouponError('');
                  }}
                  placeholder="쿠폰 코드를 입력하세요"
                  style={{ flex: 1 }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleApplyCoupon}
                  disabled={couponApplying || !couponCode.trim()}
                  style={{ flexShrink: 0, padding: '12px 16px' }}
                >
                  {couponApplying ? '확인 중...' : '적용'}
                </button>
              </div>
              {couponError && (
                <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 8 }}>
                  {couponError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Section divider */}
        <div style={{ height: 8, background: 'var(--border)' }} />

        {/* Order summary */}
        <div style={{ padding: '16px 20px' }}>
          <div className="section-title">주문 금액</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 15 }}>메뉴 금액</span>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{total.toLocaleString()}원</span>
            </div>
            {couponDiscount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--primary)', fontSize: 15, fontWeight: 600 }}>쿠폰 할인</span>
                <span style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 15 }}>-{couponDiscount.toLocaleString()}원</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>부가세 (포함)</span>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>포함</span>
            </div>
            <div className="divider" style={{ margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>합계</span>
              <span key={`total-${priceKey}`} className="price price-lg price-animated">{finalTotal.toLocaleString()}원</span>
            </div>
          </div>
        </div>

        {/* Notice */}
        <div
          style={{
            margin: '0 20px 100px',
            padding: '14px 16px',
            background: 'var(--primary-light)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
            color: 'var(--primary)',
            lineHeight: 1.6,
          }}
        >
          주문 후 취소는 매장 직원에게 문의해주세요. 조리 시작 후에는 취소가 어려울 수 있습니다.
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{
            margin: '0 20px',
            padding: '12px 16px',
            background: 'var(--danger-light)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--danger)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Order button */}
      <div className="bottom-bar" style={{ bottom: 'calc(60px + var(--safe-bottom))' }}>
        <button
          className="btn btn-primary btn-block btn-lg"
          onClick={handleSubmitOrder}
          disabled={submitting || items.length === 0}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {submitting ? (
            <>
              <span style={{ opacity: 0 }}>placeholder</span>
              <span>주문 중...</span>
              <span style={{ opacity: 0 }}>placeholder</span>
            </>
          ) : (
            <>
              <span />
              <span>주문하기</span>
              <span style={{ fontWeight: 800 }}>{finalTotal.toLocaleString()}원</span>
            </>
          )}
        </button>
      </div>

      </div>
      <BottomNav />
      <Toast {...toast} />
    </>
  );
}
